import React from 'react';
import { DisengagedReason } from '../types';

type DisengagedDictionary = {
    [key in DisengagedReason]: {
        label: string;
        resolutionSteps: React.ReactNode;
    };
};

const disengagedDictionary: DisengagedDictionary = {
    DISCONNECTED: {
        label: 'Offline',
        resolutionSteps: (
            <>
                <p>
                    To reconnect, select your Wi-Fi name and input Wi-Fi password on the smart
                    dispenser. Most routers work with Hero, but if you have trouble connecting
                    during setup, you may need to switch your bandwidth to 2.4GHz.
                </p>
            </>
        )
    },
    EXCHANGED: {
        label: 'Replacement',
        resolutionSteps: (
            <>
                <p>
                    Connect your new Hero smart dispenser to Wi-Fi, then open the Hero app and find
                    the "Set up another dispenser" option under the “More” tab. Hero will guide you
                    step-by-step, including transferring medications from the old dispenser.
                </p>
            </>
        )
    },
    DEVICE_RESET: {
        label: 'Factory Reset',
        resolutionSteps: (
            <>
                <p>
                    To reconnect, remove the travel bar, turn on the smart dispenser, and connect it
                    to Wi-Fi. Log into the Hero phone app as existing user and input the 8 digit
                    code from the smart dispenser screen. Then, set up medication list and schedule
                    in the app before adding meds to the smart dispenser.
                </p>
            </>
        )
    },
    NO_CONFIG: {
        label: 'Setup needed',
        resolutionSteps: (
            <>
                <p>To complete setup:</p>
                <ul>
                    <li>
                        <strong>Verify your email</strong> — Look for an email titled "
                        <strong>Confirm your Hero Account</strong>" and follow the instructions to
                        activate your account.
                    </li>
                    <li>
                        <strong>Add medications</strong> — You can start by adding the meds you have
                        now and easily add more later, even if you're waiting on refills.
                    </li>
                </ul>
            </>
        )
    },
    NO_PILLS: {
        label: 'No pills',
        resolutionSteps: (
            <>
                <p>
                    To add a medication, go to the "<strong>Meds</strong>" tab in the Hero app, tap
                    "<strong>+</strong>", enter the medication details, set the frequency and doses,
                    then confirm. Follow the smart dispenser’s instructions to load the medication.
                </p>
            </>
        )
    },
    NO_SCHEDULES: {
        label: 'No schedules',
        resolutionSteps: (
            <>
                <p>
                    To schedule a medication, go to the "<strong>Meds</strong>" tab, select a
                    medication, and tap "<strong>Add another schedule</strong>." Choose the
                    frequency, set the dose time and pill quantity, then repeat for other
                    medications as needed.
                </p>
            </>
        )
    },
    IN_VACATION: {
        label: 'In vacation',
        resolutionSteps: (
            <>
                <p>
                    Great job setting up Vacation Mode! When notified on the Hero phone app, tap "
                    <strong>Take</strong>" if you've taken your meds or "<strong>Skip</strong>" if
                    you need to skip. A second reminder will follow if needed—just tap "
                    <strong>Took it!</strong>" or "<strong>Skipped.</strong>"
                </p>
            </>
        )
    },
    TRANSPORT_MODE: {
        label: 'In transport',
        resolutionSteps: (
            <>
                <p>
                    Exit transport mode in a few steps: Remove the{' '}
                    <strong>L-shaped travel bar</strong>, connect Hero to Wi-Fi, and load
                    medications.
                </p>
            </>
        )
    },

    NO_ADHERENCE: {
        label: 'Missing doses',
        resolutionSteps: (
            <>
                <p>It’s been a while since you’ve used Hero — I’d like to understand why.</p>
            </>
        )
    }
};

export default disengagedDictionary;
